import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';

import { emailRegex } from 'mewe/shared/utils';
import { trimAndLower } from 'mewe/utils/miscellaneous-utils';
import FunctionalUtils from 'mewe/shared/functional-utils.js';
import tokenManager from 'mewe/shared/token-manager';
import AuthApi from 'mewe/api/auth-api';
import config from 'mewe/config';
import CodeInputManager from 'mewe/utils/code-input-manager';

export default class MwHomeResetPasswordComponent extends Component {
  challengeOrigin = 'login';
  arkoseKey = config.arkoseKeyLog;
  hcaptchaSiteKey = config.hcaptcha.login;
  hcaptchaElemClass = 'h-captcha_login';

  @tracked afterChallengeCallback;
  @tracked challengeReady;

  @tracked email;
  @tracked phoneNumber;
  @tracked phoneCode;
  @tracked invalidEmail;
  @tracked invalidPhone;
  @tracked _isEmailFlow;
  @tracked forgotPasswordSent;
  @tracked forgotPasswordPhoneCodeBlocked;
  @tracked forgotPasswordPhoneCodeSent;
  @tracked forgotPasswordError;
  @tracked sendingResetRequest;
  @tracked sendingVerifyRequest;
  @tracked isCodeComplete;

  codeInputManager = new CodeInputManager(4); // 4-digit code for password reset

  resetIdentificationIncorrectText = __(
    `This doesn't look a like a number we recognize. Please check and try again. Make sure to add your country code first.`
  );

  constructor() {
    super(...arguments);

    if (this.isEmailFlow) {
      if (this.args.parent.emailValue) {
        this.email = this.args.parent.emailValue;
      }
    } else {
      if (this.args.parent.phoneNumber) {
        this.phoneNumber = this.args.parent.phoneNumber;
      }
      if (this.args.parent.phoneCode) {
        this.phoneCode = this.args.parent.phoneCode;
      }
    }
  }

  get isEmailFlow() {
    return this._isEmailFlow ?? this.args.parent.isEmailFlow;
  }

  @action
  toggleEmailOrPhone(option) {
    this._isEmailFlow = option === 'email';
    this.forgotPasswordError = null;
  }

  get emailValue() {
    return trimAndLower(this.email);
  }

  @action
  emailUpdated() {
    const isValid = emailRegex.test(this.emailValue);
    this.invalidEmail = !isValid;
    this.forgotPasswordError = isValid ? '' : __('Please use a valid email address.');
  }

  @action
  emailKeyDown(e) {
    // prevent adding empty spaces in email input, hack for FF issue (MW-3609)
    if (e.key === ' ') {
      e.preventDefault();
    }
  }

  get phoneValue() {
    return this.phoneCode + this.phoneNumber;
  }

  @action
  phoneUpdated({ number, code, isValidNumber }) {
    this.invalidPhone = !isValidNumber;
    this.forgotPasswordError = this.invalidPhone ? __('Please enter a valid phone number.') : '';
    this.phoneCode = `+${code}`;
    this.phoneNumber = number;
  }

  get isSubmitDisabled() {
    return this.invalidEmail || this.invalidPhone || this.sendingResetRequest;
  }

  @action
  resetPassword(e) {
    e?.preventDefault?.();
    this.afterChallengeCallback = this.reset.bind(this);
  }

  reset(challengeToken, provider, onChallengeFailed) {
    if ((this.isEmailFlow && this.invalidEmail) || this.invalidPhone) {
      return;
    }

    this.forgotPasswordError = null;
    this.sendingResetRequest = true;

    const failback = (res) => {
      if (res.status === 404) {
        this.forgotPasswordError = __("We couldn't find your account with that information");
      } else if (res.status === 400 && res.data.errorCode == 117) {
        onChallengeFailed();
      } else if (res.data && res.data.blocked) {
        this.forgotPasswordError = __("You've exceeded the number of attempts. Please try again later");
      } else if (res.data?.errorCode === 100) {
        this.forgotPasswordError = this.isEmailFlow
          ? __('Please use a valid email address.')
          : __('Please enter a valid phone number.');
      } else if (res.status === 400 && res.data.message === 'Invalid phone number') {
        FunctionalUtils.error(this.resetIdentificationIncorrectText);
      } else {
        FunctionalUtils.showDefaultErrorMessage();
      }
    };

    const finalCb = () => {
      if (!this.isDestroying && !this.isDestroyed) {
        this.sendingResetRequest = false;
      }
    };

    if (this.isEmailFlow) {
      AuthApi.sendPasswordResetEmail({
        email: this.emailValue,
        session_token: challengeToken,
        challenge_provider: provider,
      })
        .then(() => {
          this.forgotPasswordSent = __(
            `Thanks! If an account exists with this email address, we'll send you an email message with instructions to reset your password.`
          );
        })
        .catch(failback)
        .finally(finalCb);
    } else {
      AuthApi.sendPasswordResetCode({
        phoneNumber: this.phoneValue,
        session_token: challengeToken,
        challenge_provider: provider,
      })
        .then((res) => {
          let response = res.data;
          if (response && response.limitReached === true) {
            this.forgotPasswordPhoneCodeBlocked = true;
          } else {
            this.forgotPasswordPhoneCodeSent = true;
          }
        })
        .catch(failback)
        .finally(finalCb);
    }
  }

  @action
  verifyCode(e) {
    e?.preventDefault?.();

    // If manual submission is required, trigger manual submit
    if (this.codeInputManager.requiresManualSubmission) {
      this.codeInputManager.manualSubmit(() => {
        // Call the actual verification logic
        this.sendingVerifyRequest = true;
        const code = this.codeInputManager.getCode();

        AuthApi.loginWithPassword({
          username: this.phoneValue,
          code: code,
        })
          .then((response) => {
            tokenManager.set(response);
            window.location = `/account/set-new-password/sms-code#${code}`;
          })
          .catch((response) => {
            if (response.data?.block) {
              this.forgotPasswordError = __('To control abuse, we limit the number of attempted logins per hour');
            } else if (response.status === 401) {
              if (response.data?.message === 'Incorrect reset password code') {
                this.forgotPasswordError = __('Incorrect reset password code');
                this.codeInputManager.setError(true);
              } else if (response.data?.message === 'Limit reached') {
                this.forgotPasswordError = __(
                  'Please try again later. To control abuse, we limit the number of attempted logins per hour'
                );
              } else {
                this.forgotPasswordError = __('Error. Please try again later.');
              }
            } else if (response.status === 400) {
              FunctionalUtils.showDefaultErrorMessage();
              window.location.reload();
            }
          })
          .finally(() => (this.sendingVerifyRequest = false));
      });
      return;
    }

    this.sendingVerifyRequest = true;

    const code = this.codeInputManager.getCode();

    AuthApi.loginWithPassword({
      username: this.phoneValue,
      code: code,
    })
      .then((response) => {
        tokenManager.set(response);
        window.location = `/account/set-new-password/sms-code#${code}`;
      })
      .catch((response) => {
        if (response.data?.block) {
          this.forgotPasswordError = __('To control abuse, we limit the number of attempted logins per hour');
        } else if (response.status === 401) {
          if (response.data?.message === 'Incorrect reset password code') {
            this.forgotPasswordError = __('Incorrect reset password code');
            this.codeInputManager.setError(true);
          } else if (response.data?.message === 'Limit reached') {
            this.forgotPasswordError = __(
              'Please try again later. To control abuse, we limit the number of attempted logins per hour'
            );
          } else {
            this.forgotPasswordError = __('Error. Please try again later.');
          }
        } else if (response.status === 400) {
          FunctionalUtils.showDefaultErrorMessage();
          window.location.reload();
        }
      })
      .finally(() => (this.sendingVerifyRequest = false));
  }

  @action
  challengeReadyCallback() {
    this.challengeReady = true;
  }

  @action
  codeFocus(event) {
    this.codeInputManager.handleFocus(event);
  }

  @action
  codeKeyDown(index, event) {
    this.codeInputManager.handleKeyDown(index, event);
  }

  @action
  codeKeyUp(index, event) {
    this.codeInputManager.handleKeyUp(index, event, (code) => {
      this.verifyCode();
    });
    this.isCodeComplete = this.codeInputManager.isComplete();
  }

  @action
  codeInput(index, event) {
    this.codeInputManager.handleInput(index, event, (code) => {
      this.verifyCode();
    });
    // Update completion state after input handling
    this.isCodeComplete = this.codeInputManager.isComplete();
    // Also update after a brief delay to ensure all processing is complete
    setTimeout(() => {
      this.isCodeComplete = this.codeInputManager.isComplete();
    }, 0);
  }

  @action
  codePaste(index, event) {
    this.codeInputManager.handlePaste(index, event, (code) => {
      this.verifyCode();
    });
    // Update completion state after paste handling
    this.isCodeComplete = this.codeInputManager.isComplete();
    // Also update after a brief delay to ensure all processing is complete
    setTimeout(() => {
      this.isCodeComplete = this.codeInputManager.isComplete();
    }, 0);
  }


  @action
  clearCodeInputs() {
    this.codeInputManager.clear();
  }

  get codeInputValues() {
    return this.codeInputManager.getValuesForTemplate();
  }
}
